import { styled } from "@mui/material";
import Button from '@mui/material/Button';
import { UPREALITY_LOGO_TEXT_GRADIENT } from "../../constants/brand";
import "./MaterialButton.css";
import { get100Color, getPlus200Color, isIpad, isMacOS, isSafari } from "../../player/Utils";
import { PlayerContext } from "../../player/PlayerContext";
import { useContext } from "react";

function MaterialButton(props) {

    const playerCtxt = useContext(PlayerContext);
    const sizeMultiplier =  playerCtxt != null ? playerCtxt.get("sizeMultiplier") : 1;

    const FilledButton = styled(Button)(({ theme }) => ({
        color: 'white',
        backgroundColor: "#2FC4B5",
        fontWeight: 'bold',
        '&:hover': {
          backgroundColor: '#00a793',
        },
      }));

    const FilledInversedButton = styled(Button)(({ theme }) => ({
        color: '#2FC4B5',
        backgroundColor: "white",        
        '&:hover': {
            backgroundColor: '#DFF5F3',
        },
    }));

    const FilledInversedNeutralButton = styled(Button)(({ theme }) => ({
        color: theme.palette.getContrastText("#FFFFFF"),
        backgroundColor: "white",
        '&:hover': {
            backgroundColor: '#00000040',
        },
    }));

    const FacebookButton = styled(Button)(({ theme }) => ({
        color: 'white',
        backgroundColor: "#1877F2",
        fontWeight: 'bold',
        '&:hover': {
            backgroundColor: '#1D64DF',
        },
    }));

    const WeTransferButton = styled(Button)(({ theme }) => ({
        color: 'white',
        backgroundColor: "#419FFF",
        '&:hover': {
            backgroundColor: '#006edb',
        },
    }));

    const TonalButton = styled(Button)(({ theme }) => ({
        color: theme.palette.getContrastText("#FFFFFF"),
        backgroundColor: "#9ae0da",
        '&:hover': {
            backgroundColor: '#7fd3ca',
        },
    }));

    const OutlinedTonalButton = styled(Button)(({ theme }) => ({
        color: theme.palette.getContrastText("#FFFFFF"),
        backgroundColor: "#FFFFFF",
        '&:hover': {
            backgroundColor: '#DFF5F3',
        },
    }));

    const BrandedFilledButton = styled(Button)(({ theme }) => ({
        color: props.color,
        backgroundColor: props.bgColor,
        '&:hover': {
          backgroundColor: getPlus200Color(props.bgColor),
        },
      }));

    const BrandedWhiteFilledButton = styled(Button)(({ theme }) => ({
        color: props.color,
        backgroundColor: 'white',
        '&:hover': {
            backgroundColor: get100Color(props.rippleColor != null ? props.rippleColor : props.color), 
        },
    }));

    const style: CSSProperties = {
        fontWeight: "bold",
        borderRadius : 20,
        padding: 8,
        paddingLeft: 16,
        paddingRight: 16,
        fontSize: 14,
        cursor: "pointer",
    };

    let safariMacSpecStyle = {};
    if (isMacOS() && isSafari() && !props.noMacSafariSpec){
        safariMacSpecStyle = {letterSpacing: -0.5, fontWeight: 900};
    }
    let iPadMinSpecStyle = {};
    if (isIpad() && sizeMultiplier < 1 && !props.noiPadMinSpec){
        iPadMinSpecStyle = {letterSpacing: -0.5 * sizeMultiplier, fontWeight: Math.round(900 * sizeMultiplier), fontSize: 17 * sizeMultiplier};
    }

    if (props.style == "outlined-primary"){
        return <Button id={props.id} variant="outlined" startIcon={props.icon != null && <img src={props.icon} style={{width: 16, height: 16, paddingBottom: 2}}/>} sx={{borderRadius: 20, paddingLeft: 2.5, paddingRight: 2.5, textTransform: 'capitalize', fontWeight: 'bold', backgroundColor: '#ffffff', '&:hover': { backgroundColor: '#f7f9fc' }, ...safariMacSpecStyle, ...props.sx}} onClick={props.onClick}>{props.value}</Button>;        
    }
    else if (props.style == "instagram-outlined"){
        style.border = "double 1px transparent";
        style.borderRadius = "20px";
        style.backgroundImage = "linear-gradient(white, white), radial-gradient(circle at top left, #f00,#3020ff)";
        style.backgroundOrigin = "border-box";
        style.backgroundClip = "padding-box, border-box";

        style.backgroundColor =  props.bgColor != null ? props.bgColor : "white";
        style.color = props.color != null ? props.color : "#79747E";  
        return <button id={props.id} className="maker-material-button" style={{...style, height: 36, textAlign: "center", display: "flex", flexDirection: "row", justifyContent: "center", alignItems: 'center', alignContent: 'center', textTransform: 'capitalize', ...safariMacSpecStyle}} onClick={props.onClick}>{props.icon && <img src={props.icon} style={{width: 16, height: 16, marginRight: 16}}/>}{props.value}</button>         
    }
    else if (props.style == "outlined-tonal"){
        return <OutlinedTonalButton id={props.id} variant="outlined" startIcon={props.icon != null && <img src={props.icon} style={{width: 16, height: 16, paddingBottom: 2}}/>} sx={{borderRadius: 20, fontWeight: 'bold', paddingLeft: 2.5, paddingRight: 2.5, textTransform: 'capitalize', ...safariMacSpecStyle, ...props.sx}} onClick={props.onClick}>{props.value}</OutlinedTonalButton>;            
    }
    else if (props.style == "filled-tonal"){
        return <TonalButton id={props.id} startIcon={props.icon != null && <img src={props.icon} style={{width: 16, height: 16, paddingBottom: 2}}/>} sx={{borderRadius: 20, fontWeight: 'bold', paddingLeft: 2.5, paddingRight: 2.5, textTransform: 'capitalize', ...safariMacSpecStyle, ...props.sx}} onClick={props.onClick}>{props.value}</TonalButton>;
    }
    else if (props.style == "facebook"){        
        return <FacebookButton id={props.id} startIcon={props.icon != null && <img src={props.icon} style={{width: 16, height: 16, paddingBottom: 2}}/>} sx={{borderRadius: 20, paddingLeft: 2.5, paddingRight: 2.5, textTransform: 'capitalize', ...safariMacSpecStyle}} onClick={props.onClick}>{props.value}</FacebookButton>;
    }
    else if (props.style == "wetransfer"){        
        return <WeTransferButton id={props.id} startIcon={props.icon != null && <img src={props.icon} style={{width: 16, height: 16, paddingBottom: 2}}/>} sx={{borderRadius: 20, paddingLeft: 2.5, paddingRight: 2.5, textTransform: 'capitalize', ...safariMacSpecStyle}} onClick={props.onClick}>{props.value}</WeTransferButton>;
    }
    else if (props.style == "filled"){
        return <FilledButton id={props.id} startIcon={props.icon != null && <img src={props.icon} style={{width: 16, height: 16, paddingBottom: 2}}/>} sx={{borderRadius: 20, paddingLeft: 2.5, paddingRight: 2.5, textTransform: 'capitalize', ...safariMacSpecStyle}} onClick={props.onClick}>{props.value}</FilledButton>;
    }
    else if (props.style == "filled-brand-gradient"){
        return <button id={props.id} className="maker-material-button maker-brand-gradient-button" style={{ "--upreality-brand-gradient": UPREALITY_LOGO_TEXT_GRADIENT, ...safariMacSpecStyle, ...props.sx }} onClick={props.onClick}>{props.icon && <img src={props.icon} style={{width: 16, height: 16, marginRight: 16}}/>}{props.value}</button>
    }
    else if (props.style == "instagram-filled"){
        style.border = "none";
        style.color = "white";
        style.background = "linear-gradient(to right, #F1000C 0%, #C20098 100%)";
        return <button id={props.id} className="maker-material-button" style={{...style, height: 36, textAlign: "center", display: "flex", flexDirection: "row", justifyContent: "center", alignItems: 'center', alignContent: 'center', textTransform: 'capitalize', ...safariMacSpecStyle}} onClick={props.onClick}>{props.icon && <img src={props.icon} style={{width: 16, height: 16, marginRight: 16}}/>}{props.value}</button> 
    }
    else if (props.style == "text"){        
        const reducedStyles = props.reducedPadding ? {display: 'inline-block', minWidth: 40, minHeight: 0, padding: 0.5, } : {};
        if (props.lowercase)
            reducedStyles.textTransform = 'lowercase';
        return <Button id={props.id} variant="text" startIcon={props.icon != null && <img src={props.icon} style={{width: 16, height: 16, paddingBottom: 2}}/>} sx={{borderRadius: 20, fontWeight: 'bold', paddingLeft: 2.5, paddingRight: 2.5, textTransform: 'capitalize', ...reducedStyles, ...safariMacSpecStyle, ...props.sx}} onClick={props.onClick}>{props.value}</Button>;
    } 
    else if (props.style == "filled-inversed"){
        return <FilledInversedButton id={props.id} variant="text" startIcon={props.icon != null && <img src={props.icon} style={{width: 16, height: 16, paddingBottom: 2}}/>} sx={{borderRadius: 20, fontWeight: 'bold', paddingLeft: 2.5, paddingRight: 2.5, textTransform: 'capitalize', ...safariMacSpecStyle}} onClick={props.onClick}>{props.value}</FilledInversedButton>;
    } 
    else if (props.style == "filled-inversed-neutral"){
        return <FilledInversedNeutralButton id={props.id} variant="text" startIcon={props.icon != null && <img src={props.icon} style={{width: 16, height: 16, paddingBottom: 2}}/>} sx={{borderRadius: 20, fontWeight: 'bold', paddingLeft: 2.5, paddingRight: 2.5, textTransform: 'capitalize', ...safariMacSpecStyle, ...props.sx}} onClick={props.onClick}>{props.value}</FilledInversedNeutralButton>;
    } 
    else if (props.style == "branded-filled"){
        return <BrandedFilledButton id={props.id} name={props.id} startIcon={props.icon != null && props.icon} sx={{width: props.width, height: props.height, borderRadius: '8px', fontFamily: "Roboto condensed", fontWeight: 'bold', fontSize: 18 * sizeMultiplier, textTransform: 'capitalize', paddingTop: 0.7, ...props.sx, ...safariMacSpecStyle, ...iPadMinSpecStyle}} onClick={props.onClick}>{props.value}</BrandedFilledButton>;
    }
    else if (props.style == "branded-white-filled"){
        return <BrandedWhiteFilledButton id={props.id} name={props.id} startIcon={props.icon != null && props.icon} sx={{width: props.width, height: props.height, borderRadius: '8px', fontFamily: "Roboto condensed", fontWeight: 'bold', fontSize: 18 * sizeMultiplier, textTransform: 'capitalize', paddingTop: 0.7, ...props.sx, ...safariMacSpecStyle, ...iPadMinSpecStyle}} onClick={props.onClick}>{props.value}</BrandedWhiteFilledButton>;
    } 
        
}

export default MaterialButton;